import React, { FC, ReactNode, useEffect, useState, memo } from 'react'
import s from './FontsProvider.module.sass'
import localFont from 'next/font/local'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'

interface Props {
  children: ReactNode
}

const FontAfishaDisplay = localFont({
  src: '../../../../public/fonts/AfishaDisplay/AfishaDisplay-Headline.woff',
  weight: '500',
  variable: '--font-a-headline',
  fallback: ['-apple-system', 'Helvetica', 'Arial', 'sans-serif'],
  display: 'swap',
  preload: false,
})

const FontSuisseIntlRegular = localFont({
  src: '../../../../public/fonts/SuisseIntl/SuisseIntl-Regular-WebXL.woff2',
  weight: '400' as const,
  variable: '--font-regular',
  fallback: ['-apple-system', 'Helvetica', 'Arial', 'sans-serif'],
  display: 'swap',
  preload: false,
})

const FontSuisseIntlMedium = localFont({
  src: '../../../../public/fonts/SuisseIntl/SuisseIntl-Medium-WebXL.woff2',
  weight: '500' as const,
  variable: '--font-medium',
  fallback: ['-apple-system', 'Helvetica', 'Arial', 'sans-serif'],
  display: 'swap',
  preload: false,
})

const FONT_CLASSES = [
  FontSuisseIntlRegular.className,
  FontAfishaDisplay.variable,
  FontSuisseIntlRegular.variable,
  FontSuisseIntlMedium.variable,
].join(' ')

/**
 * @description чтобы не было мерцания шрифтов
 */
const useMounted = () => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounted(true)
    }, 0)

    return () => clearTimeout(timer)
  }, [])

  return isMounted
}

/**
 * @description для отслеживания загрузки шрифтов
 */
const useFontsReady = () => {
  const [fontsReady, setFontsReady] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const checkFonts = async () => {
      try {
        await document.fonts.ready
        setFontsReady(true)
      } catch (error) {
        console.warn('Font loading check failed:', error)
        setFontsReady(true)
      }
    }

    checkFonts()
  }, [])

  return fontsReady
}

/**
 * @function FontsProvider - загружает шрифты
 */
const FontsProvider: FC<Props> = memo(({ children }) => {
  const isMounted = useMounted()
  const fontsReady = useFontsReady()

  return (
    <ErrorBoundary>
      <div className={FONT_CLASSES} data-fonts-ready={fontsReady} data-mounted={isMounted}>
        {children}
      </div>
    </ErrorBoundary>
  )
})

FontsProvider.displayName = 'FontsProvider'

export default FontsProvider
